import { TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS } from '@monorepo/types';
import { ModifiersZTableModel } from '../../table.modifier';
import { TTableRowNode, TTableCellTree, TTableInfo, TTableTreeModel } from '../../table.type';

type TOrderMultiFactoriesAllocationTableHeaderNode = TTableInfo<
  TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['orders'][number]
>['header']['cells'][number];

export const ORDER_MULTI_FACTORIES_ALLOCATION_TABLE_MODEL = {
  createModel: (
    headerInfo: TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['header_info'],
    orders: TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['orders'],
    isAllocationTypePercent: boolean,
    showDelete: boolean,
  ): TTableTreeModel<TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['orders'][number]> => {
    const addedFactories: string[] = Array.from(
      new Set(orders.flatMap((order) => order.factory_to_allocations.map((fa) => fa.factory_id))),
    );

    return ModifiersZTableModel.createTableModel(
      orders,
      {
        edit: {
          hasAddRow: false,
          isMulti: true,
          isEditOnly: true,
          showActionCellsInEditMode: true,
        },
        headerConfig: getHeaderConfig(headerInfo, isAllocationTypePercent, addedFactories, showDelete),
      },
      (item): TTableRowNode<TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['orders'][number]> => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRowCells(headerInfo, item, isAllocationTypePercent, addedFactories, showDelete),
        };
      },
    );
  },
};

function getHeaderConfig(
  headerInfo: TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['header_info'],
  isAllocationTypePercent: boolean,
  addedFactories: string[],
  showDelete: boolean,
): TOrderMultiFactoriesAllocationTableHeaderNode[] {
  return [
    ...headerInfo.map(
      (hi): TOrderMultiFactoriesAllocationTableHeaderNode => ({
        type: 'TEXT_CELL' as const,
        text: hi.display_name,
        widthInPx: 100,
      }),
    ),
    ...addedFactories.map(
      (factoryId): TOrderMultiFactoriesAllocationTableHeaderNode => ({
        type: 'TEXT_CELL',
        text: factoryId,
        widthInPx: 100,
        liftedInput: {
          input: {
            type: 'INPUT',
            subType: 'number',
            setValue: (value) => {
              const fData = value.factory_to_allocations.find((fta) => fta.factory_id === factoryId);
              return isAllocationTypePercent ? fData?.percentage : fData?.qty;
            },
            placeholder: isAllocationTypePercent ? 'Enter percent' : 'Enter quantity',
            min: 0,
            ...(isAllocationTypePercent && { max: 100 }),
          },
          metadata: { keyName: factoryId },
        },
      }),
    ),
    {
      type: 'TEXT_CELL',
      text: 'TOTAL',
      widthInPx: 100,
    },
    ...(showDelete
      ? [
          {
            type: 'TEXT_CELL' as const,
            text: '',
            widthInPx: 100,
          },
        ]
      : []),
  ];
}

function getRowCells(
  headerInfo: TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['header_info'],
  item: TRESPONSE_ORDER_MULTIPLE_FACTORY_ALLOCATIONS['orders'][number],
  isAllocationTypePercent: boolean,
  addedFactories: string[],
  showDelete: boolean,
): TTableCellTree[] {
  return [
    ...headerInfo.map(
      (hi): TTableCellTree => ({
        type: 'TEXT_CELL',
        text: item.order_data[hi.key],
        widthInPx: 100,
      }),
    ),

    ...addedFactories.map((factoryId): TTableCellTree => {
      const fta = item.factory_to_allocations.find((fta) => fta.factory_id === factoryId);
      return {
        type: 'TEXT_CELL',
        text: isAllocationTypePercent ? fta?.percentage || 0 : fta?.qty || 0,
      };
    }),

    {
      type: 'TEXT_CELL',
      text: item.factory_to_allocations.reduce((acc, fa) => {
        return acc + (isAllocationTypePercent ? fa.percentage : fa.qty);
      }, 0),
      widthInPx: 100,
    },
    ...(showDelete
      ? [
          {
            type: 'ACTION_CELL' as const,
            actionType: 'DELETE' as const,
            widthInPx: 100,
            asIcon: true,
          },
        ]
      : []),
  ];
}
